import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { structuredCloneUtil } from '@/utils/browser.utils';
import {
  type DataTableCellIdCoordinate,
  type DataTableCellReference,
  type DataTableCellState,
  type DataTableCellStateChangeEventDetail,
  type DataTableCellStatus,
  type DataTableCellUpsert,
  type DataTableCellUpsertError,
  type DataTableColumnApiId,
  type DataTableRowApiId,
} from '../DataTable.model';
import { DataTableCellStatusType, DataTableEvent } from '../DataTable.model';
import { toCellRef } from '../DataTable.util';

type CellState = Record<DataTableRowApiId, Record<DataTableColumnApiId, DataTableCellStatus>>;

const resetAccumulator = (acc: CellState, cell: DataTableCellUpsert | DataTableCellUpsertError): CellState => {
  const cellStatus = acc[cell.row_id]?.[cell.column_id];
  if (_notNil(cellStatus)) {
    // Create new object without mutation
    const { [cell.column_id]: _removed, ...restColumns } = acc[cell.row_id];
    if (Object.keys(restColumns).length === 0) {
      // Remove entire row if no columns left
      const { [cell.row_id]: _removedRow, ...restRows } = acc;
      return restRows;
    }
    // Update row with remaining columns
    return {
      ...acc,
      [cell.row_id]: restColumns,
    };
  }
  return acc;
};

const validationErrorAccumulator = (acc: CellState, cell: DataTableCellUpsertError): CellState => ({
  ...acc,
  [cell.row_id]: {
    ...(acc[cell.row_id] ?? {}),
    [cell.column_id]: { type: 'ValidationError', error: cell.error },
  },
});
const validationWarningAccumulator = (acc: CellState, cell: DataTableCellUpsertError): CellState => ({
  ...acc,
  [cell.row_id]: {
    ...(acc[cell.row_id] ?? {}),
    [cell.column_id]: { type: 'ValidationWarning', error: cell.error },
  },
});
const asyncValidationWarningAccumulator = (acc: CellState, cell: DataTableCellUpsertError): CellState => ({
  ...acc,
  [cell.row_id]: {
    ...(acc[cell.row_id] ?? {}),
    [cell.column_id]: { type: 'AsyncValidationWarning', error: cell.error },
  },
});

const networkErrorAccumulator = (acc: CellState, cell: DataTableCellUpsert): CellState => ({
  ...acc,
  [cell.row_id]: {
    ...(acc[cell.row_id] ?? {}),
    [cell.column_id]: { type: 'NetworkError' },
  },
});

export class DataTableCellStateService {
  #cellState: CellState = {};
  #invalidRows: Set<DataTableRowApiId> = new Set();
  #invalidColumns: Set<DataTableColumnApiId> = new Set();

  // Incremental status count tracking for O(1) cellStatusOverview()
  #statusCounts: Record<DataTableCellStatusType, number> = {
    OK: 0,
    ValidationError: 0,
    NetworkError: 0,
    ValidationWarning: 0,
    AsyncValidationWarning: 0,
  };

  constructor(private readonly eventEmitter: EventTarget) {}

  reset(cells: Array<DataTableCellUpsert>) {
    this.#handleStateUpdate(cells.reduce(resetAccumulator, this.#cellState));
  }

  validationWarning(invalidCells: Array<DataTableCellUpsertError>, resetCells: Array<DataTableCellUpsert> = []) {
    if ((invalidCells?.length ?? 0) + (resetCells?.length ?? 0) > 0) {
      const partiallyUpdatedState = resetCells.reduce(resetAccumulator, this.#cellState);
      this.#handleStateUpdate(invalidCells.reduce(validationWarningAccumulator, partiallyUpdatedState));
    }
  }

  validationError(invalidCells: Array<DataTableCellUpsertError>, resetCells: Array<DataTableCellUpsert> = []) {
    if ((invalidCells?.length ?? 0) + (resetCells?.length ?? 0) > 0) {
      const partiallyUpdatedState = resetCells.reduce(resetAccumulator, this.#cellState);
      this.#handleStateUpdate(invalidCells.reduce(validationErrorAccumulator, partiallyUpdatedState));
    }
  }

  asyncValidationErrror(invalidCells: Array<DataTableCellUpsertError>, resetCells: Array<DataTableCellUpsert> = []) {
    if ((invalidCells?.length ?? 0) + (resetCells?.length ?? 0) > 0) {
      const partiallyUpdatedState = resetCells.reduce(resetAccumulator, this.#cellState);
      this.#handleStateUpdate(invalidCells.reduce(asyncValidationWarningAccumulator, partiallyUpdatedState));
    }
  }

  networkError(unsavedCells: Array<DataTableCellUpsert>, resetCells: Array<DataTableCellUpsert> = []) {
    if ((unsavedCells?.length ?? 0) + (resetCells?.length ?? 0) > 0) {
      const partiallyUpdatedState = resetCells.reduce(resetAccumulator, this.#cellState);
      this.#handleStateUpdate(unsavedCells.reduce(networkErrorAccumulator, partiallyUpdatedState));
    }
  }

  /**
   * Calculate the delta between old and new cell states for incremental status counting.
   * Returns a record of status type changes (positive for additions, negative for removals).
   */
  #calculateDelta(oldState: CellState, newState: CellState): Record<DataTableCellStatusType, number> {
    const delta: Record<DataTableCellStatusType, number> = {
      OK: 0,
      ValidationError: 0,
      NetworkError: 0,
      ValidationWarning: 0,
      AsyncValidationWarning: 0,
    };

    // Track removed cells
    (Object.entries(oldState) as Array<[DataTableRowApiId, Record<DataTableColumnApiId, DataTableCellStatus>]>).forEach(
      ([rowId, rowData]) => {
        (Object.entries(rowData) as Array<[DataTableColumnApiId, DataTableCellStatus]>).forEach(
          ([columnId, status]) => {
            const newRowData = newState[rowId];
            const newCellStatus = _notNil(newRowData) ? newRowData[columnId] : undefined;
            if (_isNil(newCellStatus)) {
              delta[status.type] -= 1;
            }
          }
        );
      }
    );

    // Track added/changed cells
    (Object.entries(newState) as Array<[DataTableRowApiId, Record<DataTableColumnApiId, DataTableCellStatus>]>).forEach(
      ([rowId, rowData]) => {
        (Object.entries(rowData) as Array<[DataTableColumnApiId, DataTableCellStatus]>).forEach(
          ([columnId, status]) => {
            const oldRowData = oldState[rowId];
            const oldStatus = _notNil(oldRowData) ? oldRowData[columnId] : undefined;
            if (_isNil(oldStatus)) {
              // New cell with error
              delta[status.type] += 1;
            } else if (oldStatus.type !== status.type) {
              // Status type changed
              delta[oldStatus.type] -= 1;
              delta[status.type] += 1;
            }
          }
        );
      }
    );

    return delta;
  }

  #handleStateUpdate(updatedCellState: CellState) {
    if (updatedCellState !== this.#cellState) {
      // Calculate delta BEFORE updating state for incremental counting
      const delta = this.#calculateDelta(this.#cellState, updatedCellState);

      // Update cell state
      this.#cellState = updatedCellState;

      // Apply delta to incremental status counts (O(1) instead of O(n²))
      Object.entries(delta).forEach(([typeStr, count]) => {
        const type = typeStr as DataTableCellStatusType;
        this.#statusCounts[type] += count;
      });

      // Build sets for event dispatch (still need to iterate for invalid rows/columns/cells)
      const { invalidRows, invalidColumns, invalidCells } = (
        Object.entries(this.#cellState) as Array<[DataTableRowApiId, Record<DataTableColumnApiId, DataTableCellStatus>]>
      ).reduce(
        (acc, [rowId, rowData]) => {
          acc.invalidRows.add(rowId);
          (Object.entries(rowData) as Array<[DataTableColumnApiId, DataTableCellStatus]>).forEach(([columnId]) => {
            acc.invalidColumns.add(columnId);
            acc.invalidCells.add(toCellRef({ column: columnId, row: rowId }));
          });
          return acc;
        },
        {
          invalidRows: new Set<DataTableRowApiId>(),
          invalidColumns: new Set<DataTableColumnApiId>(),
          invalidCells: new Set<DataTableCellReference>(),
        }
      );

      this.#invalidRows = invalidRows;
      this.#invalidColumns = invalidColumns;
      this.eventEmitter.dispatchEvent(
        new CustomEvent<DataTableCellStateChangeEventDetail>(DataTableEvent.CellStateChange, {
          detail: {
            invalidRows,
            invalidColumns,
            invalidCells,
            overview: { ...this.#statusCounts }, // Use cached counts (O(1))
          },
        })
      );
    }
  }

  destroy() {
    this.#cellState = {};
    // Reset status counts
    this.#statusCounts = {
      OK: 0,
      ValidationError: 0,
      NetworkError: 0,
      ValidationWarning: 0,
      AsyncValidationWarning: 0,
    };
  }

  rowInvalid(row: DataTableRowApiId | undefined): boolean {
    return _notNil(row) && this.#invalidRows.has(row);
  }

  columnInvalid(column: DataTableColumnApiId | undefined) {
    return _notNil(column) && this.#invalidColumns.has(column);
  }

  hasErrors() {
    return this.#invalidRows.size > 0;
  }

  cellState(cellCoordinate: DataTableCellIdCoordinate, loaded: boolean): DataTableCellState {
    if (loaded) {
      switch (this.#cellState?.[cellCoordinate.row]?.[cellCoordinate.column]?.type) {
        case 'ValidationError':
          return 'Invalid';
        case 'ValidationWarning':
        case 'AsyncValidationWarning':
          return 'Warning';
        case 'NetworkError':
          return 'NotSaved';
        default:
          return 'Valid';
      }
    }
    return 'Loading';
  }

  cellStatus(cellCoordinate: DataTableCellIdCoordinate): DataTableCellStatus {
    return this.#cellState?.[cellCoordinate.row]?.[cellCoordinate.column] ?? { type: 'OK' };
  }

  cellStatusOverview(): Record<DataTableCellStatusType, number> {
    // O(1) return of cached counts instead of O(n²) recalculation
    return { ...this.#statusCounts };
  }

  clearColumn(...columnIds: Array<DataTableColumnApiId>) {
    columnIds.some((columnId) => {
      if (this.#invalidColumns.has(columnId)) {
        this.#handleStateUpdate(
          [...this.#invalidRows.values()].reduce((acc, rowId) => {
            delete acc[rowId][columnId];
            if (Object.keys(acc[rowId]).length === 0) {
              delete acc[rowId];
            }
            return acc;
          }, structuredCloneUtil(this.#cellState))
        );
      }
    });
  }

  clearRow(rowId: DataTableRowApiId) {
    if (this.#invalidRows.has(rowId)) {
      const updatedCellState = structuredCloneUtil(this.#cellState);
      delete updatedCellState[rowId];
      this.#handleStateUpdate(updatedCellState);
    }
  }

  clearType(...type: Array<DataTableCellStatusType>) {
    if (_isNotEmpty(type)) {
      const types = new Set(type);
      const updatedCellState = Object.entries(structuredCloneUtil(this.#cellState)).reduce<CellState>(
        (cellState, [rowId, columnState]) => {
          const rowState = Object.entries(columnState).reduce<Record<DataTableColumnApiId, DataTableCellStatus>>(
            (columnAcc, [columnId, cellError]) =>
              types.has(cellError.type) ? columnAcc : { ...columnAcc, [columnId]: cellError },
            {}
          );
          if (Object.values(rowState).length === 0) {
            return cellState;
          }
          return { ...cellState, [rowId]: rowState };
        },
        {}
      );
      this.#handleStateUpdate(updatedCellState);
    }
  }
}
